import React, { useCallback, useEffect, useRef } from "react";
import {
  EmblaCarouselType,
  EmblaEventType,
  EmblaOptionsType,
} from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import "./embla.css";
import "./catalogCard.css";

const TWEEN_FACTOR_BASE = 0.2;

export type CatalogSlide = {
  id: string;
  title: string;
  subtitle: string;
  bullets: string[];
  price: string;
  imageSrc: string;
};

type Props = {
  slides: CatalogSlide[];
  options?: EmblaOptionsType;
  onBook: (id: string) => void;
};

export default function CatalogCarousel({ slides, options, onBook }: Props) {
  const [emblaRef, emblaApi] = useEmblaCarousel(options);

  const tweenFactor = useRef(0);
  const tweenNodes = useRef<HTMLElement[]>([]);

  // --- Dots + arrows (simples, sem hooks externos)
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([]);

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setSelectedIndex(api.selectedScrollSnap());
  }, []);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi],
  );

  const setTweenNodes = useCallback((api: EmblaCarouselType): void => {
    tweenNodes.current = api.slideNodes().map((slideNode) => {
      return slideNode.querySelector(".embla__parallax__layer") as HTMLElement;
    });
  }, []);

  const setTweenFactor = useCallback((api: EmblaCarouselType) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * api.scrollSnapList().length;
  }, []);

  const tweenParallax = useCallback(
    (api: EmblaCarouselType, eventName?: EmblaEventType) => {
      const engine = api.internalEngine();
      const scrollProgress = api.scrollProgress();
      const slidesInView = api.slidesInView();
      const isScrollEvent = eventName === "scroll";

      api.scrollSnapList().forEach((scrollSnap, snapIndex) => {
        let diffToTarget = scrollSnap - scrollProgress;
        const slidesInSnap = engine.slideRegistry[snapIndex];

        slidesInSnap.forEach((slideIndex) => {
          if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

          if (engine.options.loop) {
            engine.slideLooper.loopPoints.forEach((loopItem) => {
              const target = loopItem.target();

              if (slideIndex === loopItem.index && target !== 0) {
                const sign = Math.sign(target);

                if (sign === -1)
                  diffToTarget = scrollSnap - (1 + scrollProgress);
                if (sign === 1)
                  diffToTarget = scrollSnap + (1 - scrollProgress);
              }
            });
          }

          const translate = diffToTarget * (-1 * tweenFactor.current) * 100;
          const tweenNode = tweenNodes.current[slideIndex];
          if (tweenNode)
            tweenNode.style.transform = `translateX(${translate}%)`;
        });
      });
    },
    [],
  );

  useEffect(() => {
    if (!emblaApi) return;

    setScrollSnaps(emblaApi.scrollSnapList());
    onSelect(emblaApi);

    setTweenNodes(emblaApi);
    setTweenFactor(emblaApi);
    tweenParallax(emblaApi);

    emblaApi
      .on("reInit", () => {
        setScrollSnaps(emblaApi.scrollSnapList());
        setTweenNodes(emblaApi);
        setTweenFactor(emblaApi);
        tweenParallax(emblaApi);
        onSelect(emblaApi);
      })
      .on("scroll", tweenParallax)
      .on("slideFocus", tweenParallax)
      .on("select", () => onSelect(emblaApi));
  }, [emblaApi, onSelect, setTweenNodes, setTweenFactor, tweenParallax]);

  return (
    <div className="embla catalogEmbla">
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {slides.map((s) => (
            <div className="embla__slide" key={s.id}>
              <article className="catalogCard">
                {/* Image (parallax) */}
                <div className="catalogCard__imageWrap">
                  <div className="embla__parallax">
                    <div className="embla__parallax__layer">
                      <img
                        className="catalogCard__img embla__parallax__img"
                        src={s.imageSrc}
                        alt={s.title}
                      />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="catalogCard__content">
                  <div className="catalogCard__topRow">
                    <button
                      className="catalogNavBtn"
                      onClick={scrollPrev}
                      aria-label="Previous"
                      type="button"
                    >
                      ‹
                    </button>
                    <button
                      className="catalogNavBtn"
                      onClick={scrollNext}
                      aria-label="Next"
                      type="button"
                    >
                      ›
                    </button>

                    <div className="catalogDots" aria-label="Pagination">
                      {scrollSnaps.map((_, idx) => (
                        <button
                          key={idx}
                          type="button"
                          aria-label={`Go to slide ${idx + 1}`}
                          className={
                            idx === selectedIndex
                              ? "catalogDot catalogDot--active"
                              : "catalogDot"
                          }
                          onClick={() => scrollTo(idx)}
                        />
                      ))}
                    </div>
                  </div>

                  <h2 className="catalogCard__title">{s.title}</h2>
                  <h3 className="catalogCard__subtitle">{s.subtitle}</h3>

                  <ul className="catalogCard__bullets">
                    {s.bullets.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>

                  <div className="catalogCard__bottom">
                    <div className="catalogCard__price">{s.price}</div>
                    <button
                      className="catalogCard__cta"
                      type="button"
                      onClick={() => onBook(s.id)}
                    >
                      Book Now
                    </button>
                  </div>
                </div>
              </article>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
